import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { usePageMeta } from '../lib/usePageMeta';

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    { "@type": "Question", "name": "What is a case converter tool?", "acceptedAnswer": { "@type": "Answer", "text": "A case converter tool quickly changes the letter case of text (uppercase, lowercase, sentence case, or title case) without manual editing." } },
    { "@type": "Question", "name": "When should I use a case converter?", "acceptedAnswer": { "@type": "Answer", "text": "It's useful for quickly correcting text formatting, like preparing blog titles, formatting headings, editing documents, or fixing accidental uppercase typing." } },
    { "@type": "Question", "name": "Does this tool change the meaning of my text?", "acceptedAnswer": { "@type": "Answer", "text": "No, it only changes letter casing and does not modify words, grammar, or sentence structure." } },
    { "@type": "Question", "name": "Is this case converter tool free to use?", "acceptedAnswer": { "@type": "Answer", "text": "Yes, it works directly in your browser and is completely free. You can paste or type text and convert it instantly." } },
    { "@type": "Question", "name": "Does the tool store the text I enter?", "acceptedAnswer": { "@type": "Answer", "text": "No, text is processed directly in your browser and not stored on any server, ensuring content privacy." } }
  ]
};

const faqs = [
  { q: "What is a case converter tool?", a: "A case converter tool quickly changes the letter case of text (uppercase, lowercase, sentence case, or title case) without manual editing." },
  { q: "When should I use a case converter?", a: "It's useful for quickly correcting text formatting, like preparing blog titles, formatting headings, editing documents, or fixing accidental uppercase typing." },
  { q: "Does this tool change the meaning of my text?", a: "No, it only changes letter casing and does not modify words, grammar, or sentence structure." },
  { q: "Is this case converter tool free to use?", a: "Yes, it works directly in your browser and is completely free. You can paste or type text and convert it instantly." },
  { q: "Does the tool store the text I enter?", a: "No, text is processed directly in your browser and not stored on any server, ensuring content privacy." },
];

function toSentenceCase(str) {
  return str.toLowerCase().replace(/(^\s*\w|[.!?]\s*\w)/g, c => c.toUpperCase());
}

function toTitleCase(str) {
  return str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

function toCapitalizedCase(str) {
  return str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

function toParamCase(str) {
  return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

const conversions = [
  { label: 'Sentence case', fn: toSentenceCase },
  { label: 'lower case', fn: (s) => s.toLowerCase() },
  { label: 'UPPER CASE', fn: (s) => s.toUpperCase() },
  { label: 'Title Case', fn: toTitleCase },
  { label: 'Capitalized Case', fn: toCapitalizedCase },
  { label: 'param-case', fn: toParamCase },
];

export default function CaseConverterTool() {
  const navigate = useNavigate();
  const [text, setText] = useState('');

  usePageMeta({
    title: 'Case Converter Online for Free - Wricoo',
    description: 'Convert text to uppercase, lowercase, sentence case, or title case instantly with this free case converter. Perfect for writers, editors, and SEO teams.',
    robots: 'index, follow',
    canonical: 'https://wricoo.com/tools/case-converter-online/',
  });

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'faq-schema-case-converter';
    script.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('faq-schema-case-converter');
      if (el) el.remove();
    };
  }, []);

  const handleConvert = useCallback((fn) => {
    setText(prev => fn(prev));
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="case-converter-page">

      {/* Header */}
      <header className="border-b border-blue-200 bg-blue-600 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/tools/" className="text-blue-200 hover:text-white transition"><ArrowLeft className="w-5 h-5" /></Link>
            <Link to="/" className="text-xl font-bold text-white tracking-tight">Wricoo</Link>
          </div>
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/about/" className="text-sm font-medium text-blue-100 hover:text-white transition">About</Link>
            <Link to="/pricing/" className="text-sm font-medium text-blue-100 hover:text-white transition">Pricing</Link>
            <Link to="/tools/" className="text-sm font-medium text-blue-100 hover:text-white transition">Tools</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-white hover:bg-blue-500 hover:text-white" onClick={() => navigate('/login/')}>Sign In</Button>
            <Button size="sm" className="bg-white text-blue-600 hover:bg-blue-50" onClick={() => navigate('/register/')}>Get Started</Button>
          </div>
        </div>
      </header>

      {/* Tool Section */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h1 className="font-bold text-gray-900 leading-tight mb-6 text-center">Case Converter Online for Free</h1>

          {/* Internal Links */}
          <div className="flex flex-wrap gap-3 justify-center mb-10">
            <a href="/tools/character-count-online/" className="border border-gray-200 rounded-lg px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 transition">Character Count Online</a>
            <a href="/tools/word-count-online/" className="border border-gray-200 rounded-lg px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 transition">Word Counter Online</a>
            <a href="/tools/remove-extra-spaces-online/" className="border border-gray-200 rounded-lg px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 transition">Remove Extra Spaces Online</a>
            <a href="/tools/ai-seo-content-brief-generator/" className="border border-gray-200 rounded-lg px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 transition">AI SEO Content Brief Generator</a>
          </div>

          {/* Text Input */}
          <textarea
            className="w-full h-64 border border-gray-300 rounded-lg p-4 text-gray-800 resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
            placeholder="Paste or type your text here, then select a conversion option below..."
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-testid="text-input"
          />

          {/* Conversion Buttons */}
          <div className="flex flex-wrap gap-3" data-testid="conversion-buttons">
            {conversions.map((conv, i) => (
              <Button
                key={i}
                variant="outline"
                size="sm"
                className="border-blue-200 text-blue-700 hover:bg-blue-50"
                onClick={() => handleConvert(conv.fn)}
                data-testid={`convert-btn-${i}`}
              >
                {conv.label}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-bold text-gray-900 mb-4">Convert Your Workflow, Not Just Text</h2>
          <p className="text-gray-600 leading-relaxed mb-8 max-w-2xl mx-auto">Just like you convert text formats here, you can convert your entire content process. Replace scattered spreadsheets and documents with one workspace where SEO teams, writers, and editors collaborate faster.</p>
          <Button size="lg" className="h-12 px-8" onClick={() => navigate('/pricing/')}>
            Start Your 30-Day Free Trial. No Credit Card Needed. <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="font-bold text-gray-900 mb-8">Case Converter Online FAQs</h2>
          <div className="space-y-8">
            {faqs.map((faq, i) => (
              <div key={i} data-testid={`faq-item-${i}`}>
                <h3 className="text-base font-semibold text-gray-900 mb-2">Q. {faq.q}</h3>
                <p className="text-gray-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-gray-500">&copy; {new Date().getFullYear()} Wricoo. All rights reserved.</p>
          <nav className="flex gap-4 text-sm text-gray-500">
            <a href="/about/" className="hover:text-gray-900 transition">About</a>
            <a href="/pricing/" className="hover:text-gray-900 transition">Pricing</a>
            <a href="/tools/" className="hover:text-gray-900 transition">Tools</a>
            <a href="/privacy-policy/" className="hover:text-gray-900 transition">Privacy Policy</a>
            <a href="/terms-and-conditions/" className="hover:text-gray-900 transition">Terms</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
